import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { login as apiLogin, getMe, apiLogout } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try to restore session via httpOnly cookie (primary) or localStorage token (fallback)
    getMe()
      .then((res) => {
        setUser(res.data);
      })
      .catch(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (username, password) => {
    const response = await apiLogin({ username, password });
    const { access_token, user: userData } = response.data;
    // Keep localStorage as fallback for non-cookie scenarios
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch (err) {
      // Server may already be unreachable — clear local state anyway
      console.debug('[auth] logout request failed, clearing local state:', err?.message);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Memoize the context value so consumers don't re-render on every AuthProvider render.
  const value = useMemo(() => ({ user, login, logout, loading }), [user, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
